import { TESTIMONIALS } from '../../data/testimonials'
import { useGsapContext, useGsapStagger } from '../../hooks/useGsapReveal'
import { countUp, headingReveal } from '../../animations/gsapAnimations'
import { useSection } from '../../cms/cms-context'

/* Initials are derived rather than stored — one less field to keep in sync
   with the name beside it. */
const initialsOf = (name: string) =>
  name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)

function Stars({ count = 5 }: { count?: number }) {
  return (
    <span className="flex gap-1" aria-hidden="true">
      {Array.from({ length: count }, (_, index) => (
        <svg key={index} viewBox="0 0 24 24" className="size-3.5 fill-lime">
          <path d="M12 3l2.6 5.6 6.1.8-4.5 4.2 1.2 6.1L12 16.8 6.6 19.7l1.2-6.1L3.3 9.4l6.1-.8z" />
        </svg>
      ))}
    </span>
  )
}

/* The reviews this section shipped with, in the shape the CMS reads. Six of
   them, because that is what the grid holds; the console can add a seventh
   and it will simply be shown. */
const REVIEW_ITEMS = TESTIMONIALS.slice(0, 6).map((review) => ({
  id: review.id,
  content: {
    quote: review.quote,
    name: review.name,
    vehicle: review.vehicle,
    bank: review.bank,
    rating: String(review.rating),
  },
}))

function Testimonials() {
  const cms = useSection()
  const reviews = cms.items(REVIEW_ITEMS)

  const ref = useGsapStagger<HTMLUListElement>('[data-review]', {
    y: 26,
    stagger: 0.08,
  })

  /* The headline opens like every other section's; the rating beside it
     runs up to its figure — one decimal, so it ticks rather than jumps. */
  const head = useGsapContext<HTMLDivElement>((scope) => {
    const rating = scope.querySelector<HTMLElement>('[data-rating]')
    if (rating) countUp(rating, Number(rating.dataset.rating), { decimals: 1 })
    return headingReveal(scope)
  })

  return (
    <section
      id="reviews"
      aria-labelledby="reviews-title"
      className="py-24 sm:py-32"
    >
      <div className="mx-auto max-w-[1320px] px-5 sm:px-8">
        <div
          ref={head}
          className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between"
        >
          <div className="max-w-2xl">
            <p
              data-heading="eyebrow"
              {...cms.field('eyebrow')}
              className="flex items-center gap-3 label text-lime"
            >
              <span aria-hidden="true" data-heading="rule" className="h-px w-9 coachline" />
              {cms.text('eyebrow', 'From the lounge')}
            </p>
            <h2
              id="reviews-title"
              data-heading="title"
              {...cms.field('title')}
              className="mt-5 font-display text-display-2 text-platinum"
            >
              {cms.text('title', '1,200 approvals,')}{' '}
              <span className="text-lime-grad">{cms.text('titleAccent', 'no fine print')}</span>
            </h2>
          </div>

          <div
            data-heading="body"
            className="flex shrink-0 items-center gap-5 rounded-2xl border border-lime/25 bg-lime-ink px-6 py-5"
          >
            <div>
              <p
                data-rating={cms.text('rating', '4.9')}
                {...cms.field('rating')}
                className="font-display text-4xl font-semibold text-platinum tabular-nums"
              >
                {cms.text('rating', '4.9')}
              </p>
              <Stars />
            </div>
            <div className="h-12 w-px bg-lime/25" />
            <p
              {...cms.field('ratingLabel')}
              className="max-w-[15ch] text-[12.5px] leading-relaxed text-silver"
            >
              {cms.text('ratingLabel', 'Average across 312 Google reviews')}
            </p>
          </div>
        </div>

        {/* Both the row and the card inside it carry min-w-0. Without it the
            nowrap caption at the foot of the card — a long trim name beside a
            bank — sets the card's min-content width, the flex item refuses to
            shrink under that, and on a 360px phone the whole page grows a
            sideways scrollbar instead of the line ellipsing. */}
        <ul ref={ref} className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
          {reviews.map((review) => (
            <li key={review.id} data-review {...review.props} className="flex min-w-0">
              <figure className="flex min-w-0 flex-1 flex-col rounded-2xl border border-line bg-raised p-7 shadow-card transition duration-300 hover:-translate-y-1 hover:border-lime/30 hover:shadow-lift sm:p-8">
                <span
                  aria-hidden="true"
                  className="font-display text-5xl leading-none font-semibold text-lime/30"
                >
                  &ldquo;
                </span>

                {/* A star count somebody typed as anything but a number
                    falls back to five rather than drawing nothing. */}
                <Stars count={Number(review.content.rating) || 5} />

                <blockquote className="mt-4 flex-1 text-[14.5px] leading-relaxed text-silver">
                  {review.content.quote}
                </blockquote>

                <figcaption className="mt-7 flex items-center gap-3.5 border-t border-line-soft pt-6">
                  <span className="grid size-10 shrink-0 place-items-center rounded-full border border-lime/35 bg-lime-ink text-[12.5px] font-medium text-lime-deep">
                    {initialsOf(review.content.name)}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-[14px] font-medium text-platinum">
                      {review.content.name}
                    </span>
                    {/* The trim and the bank wrap rather than ellipse: on a
                        phone the card is barely wider than the line, and the
                        bank is the half of it worth reading. */}
                    <span className="block text-[12px] leading-snug text-dim">
                      {[review.content.vehicle, review.content.bank]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                </figcaption>
              </figure>
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}

export default Testimonials
